type Listener = (...args: any[]) => void;

type EncodedBytes = {
  __type: "bytes";
  data: string;
};

type SocketEnvelope = {
  event: string;
  args?: unknown[];
  socketId?: string;
};

const bytesToBase64Url = (value: ArrayBuffer | Uint8Array) => {
  const bytes = value instanceof Uint8Array ? value : new Uint8Array(value);
  let binary = "";

  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }

  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

const base64UrlToBytes = (value: string) => {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return bytes;
};

const encodeArg = (arg: unknown): unknown => {
  if (arg instanceof ArrayBuffer || arg instanceof Uint8Array) {
    return {
      __type: "bytes",
      data: bytesToBase64Url(arg),
    } as EncodedBytes;
  }

  return arg;
};

const decodeArg = (arg: unknown): unknown => {
  if (
    arg &&
    typeof arg === "object" &&
    (arg as EncodedBytes).__type === "bytes" &&
    typeof (arg as EncodedBytes).data === "string"
  ) {
    return base64UrlToBytes((arg as EncodedBytes).data);
  }

  return arg;
};

const buildSocketUrl = (baseUrl: string, roomId: string) => {
  const url = new URL(baseUrl, window.location.href);
  url.protocol = url.protocol === "http:" ? "ws:" : "wss:";
  url.pathname = `${url.pathname.replace(/\/$/, "")}/${encodeURIComponent(
    roomId,
  )}/socket`;
  url.search = "";
  return url.toString();
};

export class CloudflareCollabSocket {
  id?: string;

  private listeners = new Map<string, Set<Listener>>();
  private readonly ws: WebSocket;
  private readonly queuedMessages: string[] = [];

  constructor(baseUrl: string, roomId: string) {
    this.ws = new WebSocket(buildSocketUrl(baseUrl, roomId));

    this.ws.addEventListener("open", () => {
      while (this.queuedMessages.length) {
        this.ws.send(this.queuedMessages.shift()!);
      }
    });

    this.ws.addEventListener("message", (event) => {
      try {
        const envelope = JSON.parse(String(event.data)) as SocketEnvelope;

        if (envelope.socketId) {
          this.id = envelope.socketId;
        }

        this.dispatch(
          envelope.event,
          ...(envelope.args ?? []).map((arg) => decodeArg(arg)),
        );
      } catch (error) {
        console.error("Failed to parse collaboration socket message", error);
      }
    });

    this.ws.addEventListener("error", () => {
      this.dispatch("connect_error");
    });
  }

  on(event: string, listener: Listener) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(listener);
    return this;
  }

  once(event: string, listener: Listener) {
    const onceListener: Listener = (...args) => {
      this.off(event, onceListener);
      listener(...args);
    };
    return this.on(event, onceListener);
  }

  off(event: string, listener?: Listener) {
    if (!listener) {
      this.listeners.delete(event);
      return this;
    }
    this.listeners.get(event)?.delete(listener);
    return this;
  }

  emit(event: string, ...args: unknown[]) {
    const message = JSON.stringify({
      event,
      args: args.map((arg) => encodeArg(arg)),
    } satisfies SocketEnvelope);

    if (this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(message);
    } else {
      this.queuedMessages.push(message);
    }

    return this;
  }

  close() {
    this.ws.close();
  }

  disconnect() {
    this.close();
  }

  private dispatch(event: string, ...args: unknown[]) {
    for (const listener of this.listeners.get(event) ?? []) {
      listener(...args);
    }
  }
}

export type CollabSocket = CloudflareCollabSocket;

export const createCloudflareCollabSocket = (baseUrl: string, roomId: string) =>
  new CloudflareCollabSocket(baseUrl, roomId);
